import { IonButton, IonCol, IonContent, IonDatetime, IonDatetimeButton, IonIcon, IonItem, IonLabel, IonList, IonModal, IonPopover, IonRouterLink, IonRow, IonSearchbar, IonSelect, IonSelectOption } from '@ionic/react';
import { DataTable } from 'src/components/shared/DataTable';
import { DataTableHeader } from 'src/components/shared/DataTable/interfaces/DataTableHeader';
import { DataTableRow } from 'src/components/shared/DataTable/interfaces/DataTableRow';
import { useApi } from 'src/hooks/useApi';
import { useUtils } from 'src/hooks/useUtils';
import { useEffect, useState, useCallback, useRef, useId } from 'react';
import { DataTablePagination } from 'src/components/shared/DataTable/interfaces/DataTablePagination';
import { useSelector } from 'react-redux';
import { AppUserSelf } from 'src/interfaces/AppUserSelf';
import { useDebounce, useDebounceWithIdle } from 'src/hooks/useDebounce';
import { SupplierSaleItem } from '../SalesPage';
import { ButtonFilter } from 'src/components/shared/ButtonFilter';
import { calendarOutline, closeOutline } from 'ionicons/icons';
import './SalesTable.css';
import moment from 'moment-timezone';

const headers: DataTableHeader[] = [
    { text: 'Item', key: 'item_description', sortable: true, align: 'left' },
    // { text: 'Account name', key: 'account_name', sortable: true, align: 'center', width: '50px' },
    { text: 'Account', key: 'account_number', sortable: true },
    // { text: 'Item uom', key: 'item_uom', sortable: true, align: 'center', width: '50px' },
    { text: 'PO Number', key: 'purchase_order_number', sortable: true, align: 'center', width: '40px' },
    { text: 'PO Date', key: 'purchase_order_date', sortable: true, align: 'center', width: '100px' },
    { text: 'Invoice Date', key: 'invoice_date', sortable: true, align: 'center', width: '100px' },
    { text: 'Invoice Number', key: 'invoice_number', sortable: true, align: 'center', width: '50px' },
    // { text: 'Order Quantity', key: 'order_quantity', sortable: true, align: 'center' },
    { text: 'Invoice Quantity', key: 'invoice_quantity', sortable: true, align: 'center', width: '40px' },
    { text: 'Item Price', key: 'item_unit_price', sortable: true, align: 'center', width: '30px' },
    { text: 'Total Sales', key: 'item_total_price', sortable: true, align: 'center', width: '30px' },
];

interface Account {
    account_name: string;
    account_number: string;
    supplier_buyer_uuid: string;
}
interface SalesTableProps {
    setDownloadSales: (queryParams: any | null) => void;
}
export const SalesTable: React.FC<SalesTableProps> = ({ setDownloadSales = () => { } }) => {
    const api = useApi();
    const utils = useUtils();
    const filterButtonId = useId();
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<SupplierSaleItem[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [pagination, setPagination] = useState<DataTablePagination>({ limit: 10, offset: 0, page: 0 });
    const [totalCount, setTotalCount] = useState<number>(0);
    const [orderBy, setOrderBy] = useState<string | null>();
    const [orderByDesc, setOrderByDesc] = useState<boolean>();
    const [search, setSearch] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState<string | null>(null);

    const [filterStartDate, setFilterStartDate] = useState<string | null>(null);
    const [filterEndDate, setFilterEndDate] = useState<string | null>(null);
    const [filterAccount, setFilterAccount] = useState<number | null>(null);
    const [filtersPopover, setFiltersPopover] = useState(false);

    const authUser: AppUserSelf | null = useSelector(
        (state: any) => state.auth.user
    );

    const startDatetime = useRef<null | HTMLIonDatetimeElement>(null);
    const endDatetime = useRef<null | HTMLIonDatetimeElement>(null);

    useEffect(() => {
        if (filterStartDate) {
            startDatetime.current?.cancel(true);
        }
    }, [filterStartDate]);

    useEffect(() => {
        if (filterEndDate) {
            endDatetime.current?.cancel(true);
        }
    }, [filterEndDate]);

    useEffect(() => {
        getAccounts()
    }, [])

    const getAccounts = () => {
        if (!authUser) return
        api.get(`supplier/${authUser.organization_id}/suppliers_sales_accounts`).then((response) => setAccounts(response.data.result))
    }

    const fetchSales = useCallback(async () => {
        if (!authUser || !pagination) {
            return;
        }

        try {
            setLoading(true);
            const response = await api.get(`supplier/${authUser.organization_id}/supplier_sales`, {
                min_date: filterStartDate && moment(filterStartDate).format('YYYY-MM-DD') || null,
                max_date: filterEndDate && moment(filterEndDate).format('YYYY-MM-DD') || null,
                supplier_buyer_uuid: filterAccount || null,
                limit: pagination.limit,
                offset: pagination.offset,
                order_by: orderBy || null,
                order_by_desc: orderByDesc,
                search: search || undefined,
            });

            if (response.status === 200) {
                if (response.data?.result) {
                    setItems(response.data.result);
                    setTotalCount(response.data.total);
                } else {
                    console.warn('Received null result from sales API');
                    setItems([]);
                    setTotalCount(0);
                }
            }
        } catch (error) {
            console.error('Failed to fetch sales:', error);
            setItems([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [api, authUser, orderBy, orderByDesc, pagination, search, filterAccount, filterEndDate, filterStartDate]);

    const getSales = useDebounce(fetchSales, 500);

    useEffect(() => {
        setDownloadSales({
            min_date: filterStartDate || null,
            max_date: filterEndDate || null,
            supplier_buyer_uuid: filterAccount || null,
            order_by: orderBy || null,
            order_by_desc: orderByDesc,
            search: search || undefined,
        });
    }, [orderBy, orderByDesc, search, filterAccount, filterEndDate, filterStartDate]);

    useEffect(() => {
        getSales();
    }, [pagination.limit, pagination.offset, orderBy, orderByDesc, search, filterAccount, filterEndDate, filterStartDate]);

    const updateSearch = useCallback((value: string) => {
        setSearch(value);
        setPagination(prev => ({ ...prev, offset: 0, page: 0 }));
    }, []);

    const handleSearch = useDebounceWithIdle(updateSearch, 500);

    const handleSearchInput = (value: string) => {
        setSearchInput(value);
        handleSearch(value);
    };

    const clearFilters = (): void => {
        setFilterStartDate(null);
        setFilterEndDate(null);
        setFilterAccount(null);
    };

    const isLessThanEndDate = (datetime: string) => {
        if (!filterEndDate) {
            return true;
        }
        const date = new Date(datetime);
        const endDate = new Date(filterEndDate);
        if (date < endDate) {
            return true;
        }
        return false;
    };

    const isGreaterThanStartDate = (datetime: string) => {
        if (!filterStartDate) {
            return true;
        }
        const date = new Date(datetime);
        const startDate = new Date(filterStartDate);
        if (date > startDate) {
            return true;
        }
        return false;
    };

    return (
        <DataTable
            title="Invoices"
            headers={headers}
            onPaginationChange={setPagination}
            loading={loading}
            orderBy={orderBy}
            orderByDesc={orderByDesc}
            setOrderBy={setOrderBy}
            setOrderByDesc={setOrderByDesc}
            itemsPerPageOptions={[5, 10, 25, 50]}
            itemsPerPage={pagination.limit}
            totalCount={totalCount}
            pagination={pagination}
            search={false}
            rows={items.map((item, index): DataTableRow => ({
                item,
                key: `${item.item_number}-${index}`,
                columns: [
                    {
                        header: 'item_description',
                        content: (
                            <div className="itemCol">
                                <IonRouterLink className="font-size-default font-weight-500" color="secondary">
                                    {item.item_description}
                                </IonRouterLink>
                                <p>
                                    {item.item_ndc && `${utils.formatNdc(item.item_ndc)}`}
                                    {item.item_number && ` | ${item.item_number}`}
                                </p>
                            </div>
                        ),
                    },
                    {
                        header: 'account_name',
                        content: <p>{item.account_name}</p>,
                    },
                    {
                        header: 'account_number',
                        content: <>
                            <p>{item.account_name}</p>
                            <p>{item.account_number}</p>
                        </>,
                    },
                    // {
                    //     header: 'item_uom',
                    //     content: <p>{item.item_uom}</p>,
                    // },
                    {
                        header: 'purchase_order_number',
                        content: <p>{item.purchase_order_number}</p>,
                    },
                    {
                        header: 'purchase_order_date',
                        content: <p>{item.purchase_order_date}</p>,
                    },
                    {
                        header: 'invoice_date',
                        content: <p>{item.invoice_date}</p>,
                    },
                    // {
                    //     header: 'order_quantity',
                    //     content: <p>{item.order_quantity}</p>,
                    // },
                    {
                        header: 'invoice_number',
                        content: <p>{item.invoice_number}</p>,
                    },
                    {
                        header: 'invoice_quantity',
                        content: <p>{item.invoice_quantity}</p>,
                    },
                    {
                        header: 'item_unit_price',
                        content: (
                            <p>{utils.toUsd(Number(item.item_unit_price), 2)}</p>
                        ),
                    },
                    {
                        header: 'item_total_price',
                        content: (
                            <p>{utils.toUsd(Number(item.item_total_price), 2)}</p>
                        ),
                    },
                ],
            }))}
        >
            <IonRow>
                <IonCol size="12" sizeSm="4">
                    {
                        filterStartDate ||
                            filterEndDate ||
                            filterAccount ? (
                            <IonButton fill="clear" size="small" onClick={clearFilters}>
                                Clear Filters
                            </IonButton>
                        ) : null}
                    <ButtonFilter
                        id={filterButtonId}
                        disabled={loading}
                        onClick={() => setFiltersPopover(true)}
                    />
                    <IonPopover
                        trigger={filterButtonId}
                        isOpen={filtersPopover}
                        triggerAction="click"
                        onIonPopoverWillDismiss={() => setFiltersPopover(false)}
                        className="popoverOrderFilters"
                    >
                        <IonContent>
                            <IonList>
                                <IonItem>
                                    <IonLabel>Start date</IonLabel>
                                    <IonDatetimeButton datetime="startDate">
                                        {!filterStartDate && (
                                            <IonIcon slot="date-target" icon={calendarOutline} />
                                        )}
                                    </IonDatetimeButton>
                                    <IonModal keepContentsMounted={true}>
                                        <IonContent>
                                            <IonDatetime
                                                id="startDate"
                                                ref={startDatetime}
                                                presentation="date"
                                                value={filterStartDate}
                                                onIonChange={(e) => {
                                                    setPagination(prev => ({ ...prev, offset: 0, page: 0 }));
                                                    setFilterStartDate(e.detail.value as string)
                                                }}
                                                isDateEnabled={isLessThanEndDate}
                                            ></IonDatetime>
                                        </IonContent>
                                    </IonModal>
                                    {filterStartDate && (
                                        <IonButton
                                            size="small"
                                            fill="clear"
                                            color="medium"
                                            onClick={() => setFilterStartDate(null)}
                                            className="ion-no-padding"
                                        >
                                            <IonIcon slot="icon-only" icon={closeOutline}></IonIcon>
                                        </IonButton>
                                    )}
                                </IonItem>
                                <IonItem>
                                    <IonLabel>End date</IonLabel>
                                    <IonDatetimeButton datetime="endDate">
                                        {!filterEndDate && (
                                            <IonIcon slot="date-target" icon={calendarOutline} />
                                        )}
                                    </IonDatetimeButton>
                                    <IonModal keepContentsMounted={true}>
                                        <IonContent>
                                            <IonDatetime
                                                id="endDate"
                                                ref={endDatetime}
                                                presentation="date"
                                                value={filterEndDate}
                                                onIonChange={(e) => {
                                                    setPagination(prev => ({ ...prev, offset: 0, page: 0 }));
                                                    setFilterEndDate(e.detail.value as string)
                                                }}
                                                isDateEnabled={isGreaterThanStartDate}
                                            ></IonDatetime>
                                        </IonContent>
                                    </IonModal>
                                    {filterEndDate && (
                                        <IonButton
                                            size="small"
                                            fill="clear"
                                            color="medium"
                                            onClick={() => setFilterEndDate(null)}
                                            className="ion-no-padding"
                                        >
                                            <IonIcon slot="icon-only" icon={closeOutline}></IonIcon>
                                        </IonButton>
                                    )}
                                </IonItem>
                                <IonItem lines="none">
                                    <div className="account-header">
                                        <IonLabel position="stacked">Account</IonLabel>
                                        {filterAccount && (
                                            <IonButton
                                                size="small"
                                                fill="clear"
                                                color="medium"
                                                onClick={() => setFilterAccount(null)}
                                                className="ion-no-padding clear-button"
                                            >
                                                <IonIcon slot="icon-only" icon={closeOutline}></IonIcon>
                                            </IonButton>
                                        )}
                                    </div>
                                    <IonSelect
                                        className="custom-select"
                                        placeholder="Filter by Account"
                                        interface="popover"
                                        interfaceOptions={{
                                            alignment: 'end',
                                            dismissOnSelect: true,
                                        }}
                                        value={filterAccount}
                                        onIonChange={(e) => {
                                            setPagination(prev => ({ ...prev, offset: 0, page: 0 }));
                                            setFilterAccount(e.detail.value)
                                        }}
                                    >
                                        {accounts?.map((account) => (
                                            <IonSelectOption
                                                key={account.account_number}
                                                value={account.supplier_buyer_uuid}
                                            >
                                                <p style={{ whiteSpace: "pre-line" }}>{account.account_name && `${account.account_name} |`}{account.account_number}</p>
                                            </IonSelectOption>
                                        ))}
                                    </IonSelect>
                                </IonItem>
                            </IonList>
                        </IonContent>
                    </IonPopover>
                </IonCol>
                <IonCol size="12" sizeSm="8">
                    <IonSearchbar
                        value={searchInput}
                        onIonChange={(e: any) => handleSearchInput(e.detail.value as string)}
                        debounce={0}
                        className="ion-text-left"
                    />
                </IonCol>
            </IonRow>
        </DataTable >
    );
};
